import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { AlertCircle, Link2 } from "lucide-react";
import { Button } from "./ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

/**
 * Iter60.p63 — Unattached payout banner shown on the Employee profile.
 *
 * Fetches every leave_payout for the employee that has no `period_id`
 * yet (unattached). Renders one banner per unattached payout with an
 * inline dropdown of open payroll periods on the employee's module +
 * an "Attach" CTA. Officer 403 on the attach endpoint (Admin/HR only).
 */
export default function UnattachedPayoutBanner({ employee, canAttach = false }) {
    const [payouts, setPayouts] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [selected, setSelected] = useState({});
    const [busy, setBusy] = useState({});

    const reload = async () => {
        if (!employee?.id) return;
        try {
            const { data } = await api.get("/leave-payouts", {
                params: { employee_id: employee.id, unattached_only: true },
            });
            setPayouts(data || []);
        } catch (e) {
            // Non-fatal for banner — swallow toast noise here.
        }
    };

    useEffect(() => { reload(); }, [employee?.id]);

    useEffect(() => {
        if (!canAttach || !employee?.module_id || payouts.length === 0) return;
        (async () => {
            try {
                const { data } = await api.get("/payroll/periods", {
                    params: { module_id: employee.module_id, status: "draft,prepared" },
                });
                const items = data?.items || data || [];
                setPeriods(items);
            } catch (e) {
                setPeriods([]);
            }
        })();
    }, [canAttach, employee?.module_id, payouts.length]);

    const attach = async (payoutId) => {
        const periodId = selected[payoutId];
        if (!periodId) {
            toast.error("Pick an open payroll period first");
            return;
        }
        setBusy((b) => ({ ...b, [payoutId]: true }));
        try {
            await api.post(`/leave-payouts/${payoutId}/attach`, { period_id: periodId });
            toast.success("Payout attached to period.");
            await reload();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy((b) => ({ ...b, [payoutId]: false }));
        }
    };

    if (payouts.length === 0) return null;

    return (
        <div
            className="rounded-md border border-[color:var(--asl-crimson)] bg-[color:var(--asl-crimson-50,#FDF2F5)] p-3 space-y-2"
            data-testid="unattached-payout-banner"
        >
            {payouts.map((p) => (
                <div
                    key={p.id}
                    className="flex items-center justify-between gap-3 flex-wrap"
                    data-testid={`unattached-payout-${p.id}`}
                >
                    <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 asl-crimson-text mt-0.5 shrink-0" />
                        <div>
                            <div className="text-sm font-semibold asl-crimson-text">
                                Unattached payout: ${Number(p.amount).toFixed(2)}
                                {" "}
                                <span className="text-xs font-normal">
                                    ({Number(p.days).toFixed(2)} days × ${Number(p.daily_rate).toFixed(2)}/day —
                                    {" "}{p.reason === "employment_end" ? "Employment end" : "Fiscal reset"})
                                </span>
                            </div>
                            <p className="text-xs text-[color:var(--asl-muted)] mt-0.5">
                                Attach to a draft/prepared payroll period below so this shows up as an allowance line.
                            </p>
                        </div>
                    </div>
                    {canAttach ? (
                        <div className="flex items-center gap-2">
                            <Select
                                value={selected[p.id] || ""}
                                onValueChange={(v) => setSelected({ ...selected, [p.id]: v })}
                            >
                                <SelectTrigger
                                    className="min-w-[220px]"
                                    data-testid={`unattached-payout-period-select-${p.id}`}
                                >
                                    <SelectValue placeholder="Pick open period…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {periods.length === 0 && (
                                        <SelectItem value="__none__" disabled>
                                            No open periods on this module
                                        </SelectItem>
                                    )}
                                    {periods.map((pd) => (
                                        <SelectItem
                                            key={pd.id}
                                            value={pd.id}
                                            data-testid={`unattached-payout-period-opt-${pd.id}`}
                                        >
                                            {pd.name || `${pd.start_date} → ${pd.end_date}`}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                onClick={() => attach(p.id)}
                                disabled={busy[p.id] || !selected[p.id]}
                                className="asl-crimson-bg text-white hover:opacity-90"
                                data-testid={`unattached-payout-attach-btn-${p.id}`}
                            >
                                <Link2 className="h-4 w-4 mr-1" />
                                {busy[p.id] ? "Attaching…" : "Attach"}
                            </Button>
                        </div>
                    ) : (
                        <span
                            className="text-xs italic text-[color:var(--asl-muted)]"
                            data-testid={`unattached-payout-locked-${p.id}`}
                        >
                            HR or Admin can attach this payout.
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}
